import React, { FocusEvent } from 'react';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  HiOutlineUserPlus,
  HiOutlinePaperAirplane,
  HiOutlineInformationCircle,
  HiOutlineCreditCard,
} from 'react-icons/hi2';
import { VALID_EMAIL_REGEX } from '../../../data-access';
import { ISeatPreview } from '../../../api';
import { FormInput, FormButton } from '../../../ui/form';
import './invite-team-member-form.scss';

interface IFormInitialData {
  email: string;
}

const createValidationScheme = (currentUserEmail?: string) => yup.object({
  email: yup
    .string()
    .required('Enter an email address')
    .matches(VALID_EMAIL_REGEX, 'Enter a valid email address')
    .test('not-self', 'You can\'t invite yourself', (value) =>
      !value || !currentUserEmail || value.toLowerCase() !== currentUserEmail.toLowerCase()
    ),
}).required();

interface IInviteTeamMemberFormProps {
  isLoading?: boolean;
  onSuccess: (formFields: IFormInitialData) => void;
  isTrial?: boolean;
  isActive?: boolean;
  totalMembers?: number;
  seatsAvailable?: number;
  currentUserEmail?: string;
  seatPreview?: ISeatPreview | null;
  isLoadingPreview?: boolean;
}

const getBillingNote = (
  isTrial: boolean,
  isActive: boolean,
  totalMembers: number,
  seatsAvailable: number,
): { text: string; type: 'info' | 'warning' } | null => {
  if (isTrial) {
    return {
      text: `Your team is on a free trial with ${totalMembers} of 3 members. New members share the trial limits.`,
      type: totalMembers >= 3 ? 'warning' : 'info',
    };
  }

  if (isActive) {
    if (seatsAvailable > 0) {
      return {
        text: `${seatsAvailable} seat${seatsAvailable > 1 ? 's' : ''} available — the new member will get full access.`,
        type: 'info',
      };
    }
    return {
      text: 'All seats are in use. The new member will join without a paid seat until you add more seats.',
      type: 'warning',
    };
  }

  return null;
};

const formatCurrency = (amount: number, currency: string = 'usd'): string => {
  const symbol = currency.toLowerCase() === 'usd' ? '$' : currency.toUpperCase() + ' ';
  return `${symbol}${amount.toFixed(2)}`;
};

export const InviteTeamMemberForm = ({
  isLoading = false,
  onSuccess,
  isTrial = false,
  isActive = false,
  totalMembers = 0,
  seatsAvailable = 0,
  currentUserEmail,
  seatPreview,
  isLoadingPreview = false,
}: IInviteTeamMemberFormProps): JSX.Element => {
  const form = useForm<IFormInitialData>({
    mode: 'onBlur',
    defaultValues: { email: '' },
    resolver: yupResolver(createValidationScheme(currentUserEmail)),
  });
  const { control, handleSubmit, setValue, formState: { errors } } = form;

  const showSeatCost = isActive && !isTrial && seatsAvailable <= 0 && seatPreview;
  const billingNote = showSeatCost ? null : getBillingNote(isTrial, isActive, totalMembers, seatsAvailable);
  const intervalLabel = seatPreview?.interval === 'yearly' ? 'yr' : 'mo';

  return (
    <div className="invite-team-member">
      <div className="invite-team-member__icon">
        <HiOutlineUserPlus size={26} />
      </div>
      <h3 className="invite-team-member__title">Invite Team Member</h3>
      <p className="invite-team-member__desc">
        They'll receive a magic link to join your team as a <strong>Member</strong>. You can change their role later.
      </p>

      {isActive && !isTrial && seatsAvailable <= 0 && isLoadingPreview && (
        <div className="invite-team-member__note invite-team-member__note--billing">
          <div className="seat-cost">
            <div className="seat-cost__shimmer" />
            <div className="seat-cost__shimmer seat-cost__shimmer--short" />
          </div>
        </div>
      )}

      {showSeatCost && (
        <div className="invite-team-member__note invite-team-member__note--billing">
          <HiOutlineCreditCard size={16} />
          <div className="seat-cost">
            <span className="seat-cost__title">+1 seat will be added</span>
            <div className="seat-cost__row">
              <span className="seat-cost__label">Charged now (prorated)</span>
              <span className="seat-cost__value">{formatCurrency(seatPreview.proration_amount, seatPreview.currency)}</span>
            </div>
            <div className="seat-cost__divider" />
            <div className="seat-cost__row">
              <span className="seat-cost__label">New {seatPreview.interval === 'yearly' ? 'yearly' : 'monthly'} total</span>
              <span className="seat-cost__value">{formatCurrency(seatPreview.new_total, seatPreview.currency)}/{intervalLabel}</span>
            </div>
          </div>
        </div>
      )}

      {!showSeatCost && !isLoadingPreview && billingNote && (
        <div className={`invite-team-member__note invite-team-member__note--${billingNote.type}`}>
          <HiOutlineInformationCircle size={16} />
          <span>{billingNote.text}</span>
        </div>
      )}

      <form className="invite-member-form" onSubmit={handleSubmit(onSuccess)}>
        <div className="invite-member-form__field">
          <label className="invite-member-form__label">Email address</label>
          <Controller
            control={control}
            name="email"
            render={({ field: { name, onBlur, value, ref } }) => (
              <FormInput
                ref={ref}
                name={name}
                placeholder="colleague@company.com"
                error={errors?.email?.message}
                value={value}
                disabled={isLoading}
                onBlur={onBlur}
                onChange={(e: FocusEvent<HTMLInputElement>) => setValue('email', e?.target?.value)}
              />
            )}
          />
        </div>
        <FormButton theme="green-light" loading={isLoading} disabled={isLoading || isLoadingPreview} htmlType="submit">
          <HiOutlinePaperAirplane size={16} />
          {showSeatCost ? 'Add Seat & Send Invite' : 'Send Invite'}
        </FormButton>
      </form>
    </div>
  );
}
